import { ClipboardCopyButton } from '@neo/blocks/clipboard-copy-button'
import { cn } from '@neo/lib/utils'
import { Button } from '@neo/primitives/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@neo/primitives/dialog'
import { Loader } from '@neo/primitives/loader'
import { ScrollArea } from '@neo/primitives/scroll-area'
import { TruncatedTextWithTooltip } from '@neo/primitives/truncated-text-with-tooltip'
import { Expand, ImageOff, Info } from 'lucide-react'
import { useMemo, useState } from 'react'
import { CodeBlock } from 'v2/pages/contacts/data-capture-setup/components/code-block'
import { FormattedMessage } from 'v2/shared/components/formatted-message'
import { AudioPlayer } from 'v2/shared/components/media/audio-player'
import { logger } from 'v2/utils/logger'
import { NodeName } from '../components/node-name'
import type { NodeConfig, NodeExecutedEvent } from '../types'
import { SkillNodeHeader } from './header'

interface ExpandableViewProps {
  data: string | object | undefined | null
  title: string
  maxLength?: number
  isMedia?: boolean
  size?: 'icon' | 'default'
  icon?: React.ReactNode
}

const renderClipboardButton = (textToCopy: string, key: string) => (
  <ClipboardCopyButton
    textToCopy={textToCopy}
    tooltipText={`Copy ${key}`}
    aria-label={`Copy ${key}`}
    position="topRight"
    className="border border-border bg-card text-primary"
    data-testid={`button-copy-${key}`}
  />
)

type MediaType = 'image' | 'audio' | 'text'

interface MediaData {
  type: MediaType
  jsonString: string
  prettyJson: string
  markdown: boolean
}

const useMediaTypeDetection = (
  data: string | object | undefined | null,
  isMedia: boolean,
): MediaData =>
  useMemo(() => {
    if (!data) return { type: 'text', jsonString: '', prettyJson: '', markdown: false }

    const isJson = typeof data === 'object'
    const markdown = !isJson
    const jsonStr = isJson ? JSON.stringify(data) : String(data)

    let prettyStr = jsonStr
    if (!markdown) {
      try {
        const parsed = typeof data === 'string' ? JSON.parse(data) : data
        prettyStr = JSON.stringify(parsed, null, 2)
      } catch {
        prettyStr = jsonStr
      }
    }

    // Determine media type for media content
    let type: MediaType = 'text'
    if (isMedia && typeof data === 'string') {
      try {
        const url = new URL(data)
        const isValidUrl = url.protocol === 'http:' || url.protocol === 'https:'
        if (isValidUrl) {
          if (/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url.pathname)) {
            type = 'image'
          } else if (/\.(mp3|wav|ogg|m4a|aac)$/i.test(url.pathname)) {
            type = 'audio'
          }
        }
      } catch {
        // Keep default 'text' type
      }
    }

    return { type, jsonString: jsonStr, prettyJson: prettyStr, markdown }
  }, [data, isMedia])

interface MediaViewerProps {
  data: string
  prettyJson: string
  type: MediaType
}

const MediaViewer = ({ data, prettyJson, type }: MediaViewerProps) => {
  const [imageLoading, setImageLoading] = useState(true)
  const [imageFailed, setImageFailed] = useState(false)

  if (type === 'image') {
    return (
      <div className="relative group flex items-center justify-center min-h-[200px] w-[min(40rem,calc(100vw-5rem))] max-w-full rounded-md border border-border bg-card">
        {imageLoading && (
          <div
            className="absolute inset-0 flex items-center justify-center rounded-md"
            aria-live="polite"
            aria-busy="true"
          >
            <Loader text="Loading image..." variant="circle" />
          </div>
        )}
        {imageFailed && (
          <output
            className="flex flex-col items-center gap-2 p-6 font-inter text-s-400 text-secondary text-center"
            data-testid="skill-image-unavailable"
          >
            <ImageOff className="size-6" aria-hidden />
            <span>Image preview is unavailable.</span>
          </output>
        )}
        <img
          src={data}
          alt="Generated media"
          className={cn(
            'max-w-full max-h-[65vh] object-contain rounded-md',
            imageFailed && 'hidden',
          )}
          onLoad={() => {
            setImageLoading(false)
            setImageFailed(false)
          }}
          onError={(e) => {
            logger.error('Error loading image', e)
            setImageLoading(false)
            setImageFailed(true)
          }}
        />
        {renderClipboardButton(prettyJson, 'image-url')}
      </div>
    )
  }

  if (type === 'audio') {
    return (
      <div className="relative group w-[min(32rem,calc(100vw-5rem))] max-w-full overflow-x-auto rounded-md border border-border bg-card p-6">
        <div className="flex justify-center">
          <AudioPlayer url={data} fileName="Generated Audio" className="w-full" />
        </div>
        {renderClipboardButton(prettyJson, 'audio-url')}
      </div>
    )
  }

  return null
}

interface JsonViewerProps {
  prettyJson: string
  markdown: boolean
}

const JsonViewer = ({ prettyJson, markdown }: JsonViewerProps) => (
  <ScrollArea viewportClassName="max-h-[60vh]">
    <div className="relative group">
      {markdown ? (
        <>
          <div className="font-inter text-s-400 text-primary p-4 pr-10 whitespace-normal break-words [overflow-wrap:anywhere] [&_pre]:my-3 [&_pre]:whitespace-pre [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:border [&_pre]:border-border [&_pre]:bg-hv1 [&_pre]:p-3 [&_code]:font-jetbrains [&_code]:text-xs-400 [&_table]:block [&_table]:w-full [&_table]:overflow-x-auto [&_table]:text-left [&_table]:text-s-400 [&_th]:border-b [&_th]:border-border [&_th]:p-2 [&_td]:border-b [&_td]:border-border [&_td]:p-2">
            <FormattedMessage content={prettyJson} markdown disallowedElements={[]} />
          </div>
          {renderClipboardButton(prettyJson, 'result')}
        </>
      ) : (
        <>
          <div className="[&_pre]:m-0 [&_pre]:rounded-none [&_pre]:border-0">
            <CodeBlock code={prettyJson} language="json" showCopyButton={false} />
          </div>
          {renderClipboardButton(prettyJson, 'json')}
        </>
      )}
    </div>
  </ScrollArea>
)

const ExpandableView = ({
  data,
  title,
  maxLength = 100,
  isMedia = false,
  size = 'default',
  icon = <Info className="h-3 w-3" />,
}: ExpandableViewProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const mediaData = useMediaTypeDetection(data, isMedia)

  if (!data) return <span className="text-muted-foreground">-</span>

  const truncatedData =
    mediaData.jsonString.length > maxLength
      ? `${mediaData.jsonString.substring(0, maxLength)}...`
      : mediaData.jsonString

  const isMediaContent = mediaData.type === 'image' || mediaData.type === 'audio'

  return (
    <div className="flex items-start gap-2">
      {size === 'default' && (
        <div
          className={cn(
            'flex-1 text-xs-400 text-primary whitespace-break-spaces break-all overflow-hidden',
            mediaData.markdown && !isMediaContent ? 'font-inter' : 'font-jetbrains',
          )}
        >
          {truncatedData}
        </div>
      )}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogTrigger asChild>
          <Button
            variant="ghost"
            size="iconXs"
            className="shrink-0 text-secondary"
            aria-label={`Expand ${title}`}
            data-testid={isMedia ? 'button-expand-media' : 'button-expand-json'}
          >
            {icon}
          </Button>
        </DialogTrigger>
        <DialogContent
          className={cn(
            isMediaContent
              ? 'max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-hidden w-fit'
              : 'w-[calc(100vw-2rem)] max-w-4xl max-h-[80vh] overflow-hidden',
          )}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          <div
            className={cn(
              !isMediaContent && 'rounded-md border border-border bg-card overflow-hidden',
            )}
          >
            {isMediaContent ? (
              <MediaViewer
                data={data as string}
                prettyJson={mediaData.prettyJson}
                type={mediaData.type}
              />
            ) : (
              <JsonViewer prettyJson={mediaData.prettyJson} markdown={mediaData.markdown} />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export const getSkillResultView = (
  result: string | object | undefined | null,
  isMediaSkill: boolean,
  size: 'icon' | 'default' = 'default',
): React.ReactNode => {
  if (!result) return <span className="text-muted-foreground">No result found</span>

  return (
    <ExpandableView
      key="skill-result"
      data={result}
      title="Skill Result"
      isMedia={isMediaSkill}
      size={size}
    />
  )
}

export const SkillResultAnalysis = ({ node }: { node: NodeExecutedEvent }) => {
  const nodeConfig = (node.node_config || {}) as NodeConfig
  const isMediaSkill = ['image_generation', 'text_to_speech', 'audio_generation'].includes(
    nodeConfig.skill_type as string,
  ) as boolean

  const valueMap = new Map<string, string | React.ReactNode | undefined>([
    [
      'Executed skill node',
      <NodeName name={node.current_node} type={node.current_node_type} key="current-node" />,
    ],
    [
      'Previous node',
      <NodeName name={node.previous_node} type={node.previous_node_type} key="previous-node" />,
    ],
    ['Chosen path', node.chosen_path],
    [
      'Skill type',
      <TruncatedTextWithTooltip
        key="skill-type"
        tooltipContent={
          <span className="font-jetbrains text-xs-400">{nodeConfig.skill_type as string}</span>
        }
      >
        {nodeConfig.skill_type as string}
      </TruncatedTextWithTooltip>,
    ],
    ['Skill status', nodeConfig.skill_status as string],
    [
      'Skill input',
      <ExpandableView
        key="skill-input"
        data={nodeConfig.skill_input}
        title="Skill Input"
        icon={<Expand className="h-3 w-3" />}
      />,
    ],
    ['Skill result', getSkillResultView(nodeConfig.skill_result, isMediaSkill)],
    ['Time taken', nodeConfig.time_taken ? `${Number(nodeConfig.time_taken).toFixed(3)}s` : '-'],
  ])

  return (
    <div className="flex flex-col gap-2">
      <SkillNodeHeader node={node} />
      <div className="flex flex-col gap-4 border-t border-border px-2 py-3">
        {Array.from(valueMap.entries()).map(([key, value]) => (
          <div key={key} className="flex items-start gap-2">
            <div className="font-inter text-s-400 text-secondary w-28 shrink-0">{key}:</div>
            <div
              className={cn(
                'text-primary min-w-0 flex-1 whitespace-break-spaces break-words',
                ['Executed skill node', 'Previous node', 'Chosen path'].includes(key)
                  ? 'font-inter text-s-400'
                  : 'font-jetbrains text-xs-400',
              )}
            >
              {value}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
